import React, { createContext, useContext, useState, useEffect, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';
import '@/App.css';
import { useReferralCapture } from './hooks/useReferralCapture';
import HomePage from './pages/HomePage';
import TourDetailPage from './pages/TourDetailPage';
import MyToursPage from './pages/MyToursPage';
import TourPlayerPage from './pages/TourPlayerPage';
import InviteGuestsPage from './pages/InviteGuestsPage';
import GuestJoinPage from './pages/GuestJoinPage';
import AdminDashboard from './pages/AdminDashboard';
import CreateTourPage from './pages/CreateTourPage';
import EditTourPage from './pages/EditTourPage';
import UploadAudioPage from './pages/UploadAudioPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PromoCodesPage from './pages/PromoCodesPage';
import PaymentManagementPage from './pages/PaymentManagementPage';
import ProfilePage from './pages/ProfilePage';
import ContactSubmissionsPage from './pages/ContactSubmissionsPage';
import DiagnosticsPage from './pages/DiagnosticsPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import AdminBlogPage from './pages/AdminBlogPage';
import UnsubscribePage from './pages/UnsubscribePage';
import AdminTestimonialsPage from './pages/AdminTestimonialsPage';
import AdminTestSettingsPage from './pages/AdminTestSettingsPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AdminOrganizationsPage from './pages/AdminOrganizationsPage';
import AdminBadgesPage from './pages/AdminBadgesPage';
import AdminSocialAnalyticsPage from './pages/AdminSocialAnalyticsPage';
import AdminReportDeliveriesPage from './pages/AdminReportDeliveriesPage';
import LeaderboardPage from './pages/LeaderboardPage';
import PassportPage from './pages/PassportPage';
import TravelerPermitPage from './pages/StridesShopPreview';
import AdminReviewsPage from './pages/AdminReviewsPage';
import AdminStridesRulesPage from './pages/AdminStridesRulesPage';
import ForPartnersPage from './pages/ForPartnersPage';
import AdminPartnerLeadsPage from './pages/AdminPartnerLeadsPage';
import AdminAnalyticsOffersPage from './pages/AdminAnalyticsOffersPage';
import AnalyticsOfferAcceptPage from './pages/AnalyticsOfferAcceptPage';
import AnalyticsOfferSuccessPage from './pages/AnalyticsOfferSuccessPage';
import SubscriptionManagePage from './pages/SubscriptionManagePage';
import AdminAccountingPage from './pages/AdminAccountingPage';
import FinanceDashboard from './pages/finance/FinanceDashboard';
import FinanceAdvertisersPage from './pages/finance/FinanceAdvertisersPage';
import FinanceAnalyticsClientsPage from './pages/finance/FinanceAnalyticsClientsPage';
import FinanceMerchandisePage from './pages/finance/FinanceMerchandisePage';
import {
  FinanceOrdersPage,
  FinanceInvoicesPage,
  FinanceRevenuePage,
  FinanceReportsPage,
  FinanceExpensesPage,
  FinanceSettingsPage,
  FinanceInboxPage,
  FinanceContractsPage,
  FinanceTaxPage,
  FinanceForecastPage,
} from './pages/finance/FinanceOtherPages';
import MobileNav from './components/MobileNav';

// Error Boundary to prevent white screens
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Something went wrong</h2>
            <p className="text-slate-600 mb-6">We're sorry, an error occurred. Please try again.</p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.href = '/';
              }}
              className="w-full bg-teal-500 hover:bg-teal-600 text-white py-3 rounded-full font-semibold transition-all"
            >
              Go to Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/** Mounts once inside BrowserRouter; captures `?ref=CODE` on any landing. */
function ReferralCaptureShell() {
  useReferralCapture(API);
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      // Hard 10s timeout so a slow backend can't pin the global loading spinner forever
      const response = await axios.get(`${API}/auth/me`, { timeout: 10000 });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AuthContext.Provider value={{ user, token, login, logout }}>
        <BrowserRouter>
          <ReferralCaptureShell />
          <div className="App">
            <div className="pb-20">
              <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/diag" element={<DiagnosticsPage />} />
              <Route path="/tours/:tourId" element={<TourDetailPage />} />
              <Route
                path="/my-tours"
                element={token ? <MyToursPage /> : <Navigate to="/" />}
              />
              <Route
                path="/profile"
                element={token ? <ProfilePage /> : <Navigate to="/" />}
              />
              <Route
                path="/tour-player/:purchaseId"
                element={token ? <TourPlayerPage /> : <Navigate to="/" />}
              />
              <Route
                path="/invite-guests"
                element={token ? <InviteGuestsPage /> : <Navigate to="/" />}
              />
              <Route
                path="/guest-join"
                element={<GuestJoinPage />}
              />
              <Route
                path="/admin"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AdminDashboard />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/create-tour"
                element={
                  token && user?.account_type === 'admin' ? (
                    <CreateTourPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/upload-audio"
                element={
                  token && user?.account_type === 'admin' ? (
                    <UploadAudioPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/edit-tour/:tourId"
                element={
                  token && user?.account_type === 'admin' ? (
                    <EditTourPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/promo-codes"
                element={
                  token && user?.account_type === 'admin' ? (
                    <PromoCodesPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/payment-success"
                element={token ? <PaymentSuccessPage /> : <Navigate to="/" />}
              />
              <Route
                path="/admin/payments"
                element={
                  token && user?.account_type === 'admin' ? (
                    <PaymentManagementPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/contacts"
                element={
                  token && user?.account_type === 'admin' ? (
                    <ContactSubmissionsPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/passport/me" element={<PassportPage />} />
              <Route path="/passport/user/:userId" element={<PassportPage />} />
              {/* Traveler Permit / Strides Shop — cosmetics for the avatar
                  used on the passport + leaderboard + share cards. */}
              <Route path="/strides-shop-preview" element={<TravelerPermitPage />} />
              <Route path="/traveler-permit" element={<TravelerPermitPage />} />
              <Route path="/blog/:blogId" element={<BlogPostPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/for-partners" element={<ForPartnersPage />} />
              <Route path="/analytics-offer/:token" element={<AnalyticsOfferAcceptPage />} />
              <Route path="/analytics-offer/:token/success" element={<AnalyticsOfferSuccessPage />} />
              <Route path="/subscription/manage/:subId/:token" element={<SubscriptionManagePage />} />
              <Route
                path="/admin/blog"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AdminBlogPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/testimonials"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AdminTestimonialsPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/test-settings"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AdminTestSettingsPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AnalyticsDashboard />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/organizations"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AdminOrganizationsPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/badges"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AdminBadgesPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/social-analytics"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AdminSocialAnalyticsPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/report-deliveries"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AdminReportDeliveriesPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/reviews"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AdminReviewsPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/partner-leads"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AdminPartnerLeadsPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/analytics-offers"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AdminAnalyticsOffersPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/accounting"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AdminAccountingPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/strides-rules"
                element={
                  token && user?.account_type === 'admin' ? (
                    <AdminStridesRulesPage />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              {/* New Finance OS */}
              <Route path="/admin/finance" element={token && user?.account_type === 'admin' ? <FinanceDashboard /> : <Navigate to="/" />} />
              <Route path="/admin/finance/advertisers" element={token && user?.account_type === 'admin' ? <FinanceAdvertisersPage /> : <Navigate to="/" />} />
              <Route path="/admin/finance/analytics-clients" element={token && user?.account_type === 'admin' ? <FinanceAnalyticsClientsPage /> : <Navigate to="/" />} />
              <Route path="/admin/finance/merchandise" element={token && user?.account_type === 'admin' ? <FinanceMerchandisePage /> : <Navigate to="/" />} />
              <Route path="/admin/finance/orders" element={token && user?.account_type === 'admin' ? <FinanceOrdersPage /> : <Navigate to="/" />} />
              <Route path="/admin/finance/invoices" element={token && user?.account_type === 'admin' ? <FinanceInvoicesPage /> : <Navigate to="/" />} />
              <Route path="/admin/finance/revenue" element={token && user?.account_type === 'admin' ? <FinanceRevenuePage /> : <Navigate to="/" />} />
              <Route path="/admin/finance/reports" element={token && user?.account_type === 'admin' ? <FinanceReportsPage /> : <Navigate to="/" />} />
              <Route path="/admin/finance/expenses" element={token && user?.account_type === 'admin' ? <FinanceExpensesPage /> : <Navigate to="/" />} />
              <Route path="/admin/finance/settings" element={token && user?.account_type === 'admin' ? <FinanceSettingsPage /> : <Navigate to="/" />} />
              <Route path="/admin/finance/inbox" element={token && user?.account_type === 'admin' ? <FinanceInboxPage /> : <Navigate to="/" />} />
              <Route path="/admin/finance/contracts" element={token && user?.account_type === 'admin' ? <FinanceContractsPage /> : <Navigate to="/" />} />
              <Route path="/admin/finance/tax" element={token && user?.account_type === 'admin' ? <FinanceTaxPage /> : <Navigate to="/" />} />
              <Route path="/admin/finance/forecast" element={token && user?.account_type === 'admin' ? <FinanceForecastPage /> : <Navigate to="/" />} />
            </Routes>
          </div>
          {token && <MobileNav />}
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export default App;